import { Box, Collapse, Dialog, DialogContent, DialogProps, IconButton, Paper, Stack, Table, TableBody, TableCell, tableCellClasses, TableContainer, TableRow, Typography } from '@mui/material';
import { StudentResult } from '../../../hooks/types/result';
import { ResultsSummaryCard } from './ResultsSummaryCard';
import CloseIcon from '@mui/icons-material/Close';
import PieChartIcon from '@mui/icons-material/PieChart';
import TimerIcon from '@mui/icons-material/Timer';
import Grid from '@mui/material/Grid';
import { formatDuration } from '../format';
import { useTranslation } from 'react-i18next';
import React, { useMemo } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Slide } from '@/components/slides/Slide';
import { SLIDE_PREVIEW_SIZE } from '@/constant';
import { ResultsDisplay } from './ResultsDisplay';
import { QuestionSlide } from '@/types/slide';
import { useMedia } from '@/hooks/useMedia/useMedia';

function Row(props: { slide: QuestionSlide; index: number }) {
    const { slide } = props;
    const { t } = useTranslation();
    const { isMobile } = useMedia();
    const [open, setOpen] = React.useState(true);

    const timeSpent = useMemo(
        () =>
            formatDuration(
                slide.questions.reduce((total, question) => {
                    return total + question.answers.reduce((total, answer) => total + (new Date(answer.endTime).getTime() - new Date(answer.startTime).getTime()), 0);
                }, 0) / 1000,
            ),
        [slide.questions],
    );

    return (
        <React.Fragment>
            <TableRow
                sx={{
                    [`& .${tableCellClasses.root}`]: {
                        borderBottom: 'none',
                    },
                    ':hover': {
                        cursor: 'pointer',
                    },
                }}
                hover
                onClick={() => setOpen(!open)}
            >
                <TableCell component='th' scope='row' sx={{ width: '60%' }}>
                    {t('result.label.slide')} ({props.index + 1})
                </TableCell>
                <TableCell sx={{ width: '20%' }}>
                    {slide.score} / {slide.questions.length}
                </TableCell>
                <TableCell align='right' sx={{ width: '20%' }}>
                    <IconButton aria-label='expand row' size='small' onClick={() => setOpen(!open)}>
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ padding: 0 }} colSpan={6}>
                    <Collapse in={open} timeout='auto' unmountOnExit>
                        <Stack spacing={2} direction={isMobile ? 'column' : 'row'} sx={{ py: 2 }}>
                            <Stack spacing={2} sx={{ alignItems: 'center' }}>
                                <div
                                    style={{
                                        height: SLIDE_PREVIEW_SIZE.height,
                                        width: SLIDE_PREVIEW_SIZE.width,
                                    }}
                                >
                                    <Slide components={slide.data} questions={slide.questions} preview={{ answer: false }} />
                                </div>
                                <Box sx={{ px: 2 }}>
                                    <Typography>
                                        {t('result.label.timeSpent')}: {timeSpent}
                                    </Typography>
                                </Box>
                            </Stack>
                            {slide.questions.length > 0 && (
                                <Box sx={{ overflowX: 'auto' }}>
                                    <ResultsDisplay slide={slide} />
                                </Box>
                            )}
                        </Stack>
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    );
}

/**
 * Display the result per slide
 * @param slides - The slides to display
 * @returns -
 */
function CollapsibleResultsTable({ slides }: { slides: QuestionSlide[] }) {
    return (
        <TableContainer component={Paper} sx={{ backgroundColor: 'transparent' }}>
            <Table aria-label='collapsible table' sx={{ tableLayout: 'fixed' }}>
                <TableBody>
                    {slides.map((slide, index) => (
                        <Row key={slide.id} slide={slide} index={index} />
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

export const StudentResultsDialog: React.FC<DialogProps & { result: StudentResult }> = ({ result, ...props }) => {
    const { t } = useTranslation();
    const { isMobile } = useMedia();

    return (
        <Dialog fullScreen sx={() => (isMobile ? { '& .MuiDialog-paper': { borderRadius: 0 } } : { px: 4, py: 6 })} {...props}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box sx={{ mx: 3, my: 2 }}>
                    <Typography variant='caption' sx={{ fontSize: 14 }}>
                        {t('student.title')}
                    </Typography>
                    <Typography variant='h4'>
                        {result.studentName}
                        {result.studentNumber ? ` (${result.studentNumber})` : ''}
                    </Typography>
                </Box>
                <IconButton aria-label='close' onClick={(event) => props.onClose && props.onClose(event, 'backdropClick')} sx={{ m: 2, height: 40, width: 40 }}>
                    <CloseIcon />
                </IconButton>
            </Box>
            <DialogContent>
                <Stack spacing={2}>
                    <Grid container rowSpacing={1} columnSpacing={1} columns={{ xs: 6, sm: 6, md: 12 }}>
                        <Grid size={6}>
                            <ResultsSummaryCard icon={<PieChartIcon />} title={t('result.label.score')} value={`${result.score.toString()} / ${result.questionCount}`} />
                        </Grid>
                        <Grid size={6}>
                            <ResultsSummaryCard icon={<TimerIcon />} title={t('result.label.timeSpent')} value={formatDuration(result.timeSpent / 1000)} />
                        </Grid>
                    </Grid>
                    <CollapsibleResultsTable slides={result.slides} />
                </Stack>
            </DialogContent>
        </Dialog>
    );
};
